/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import React from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './LoanDataDisplay.scss';
import fmt from 'indian-number-format';

// Define TypeScript interfaces
interface RefinancingReportItem {
  loanId: string;
  branch: string;
  borrowerName: string;
  dateOfOriginalLoan: string;
  amountOfOriginalLoan: number;
  pledgeNumber: string;
  refinancedAmount: number;
  refinancedDate: string | null;
  currentPrincipalOutstanding: number;
  currentInterestOutstanding: number;
  totalOutstanding: number;
  nextDueDate: string | null;
  lastPaidDate: string | null;
  lastPaidAmount: number;
  defaultDays: number | null;
}

interface RefinancingReportProps {
  data: RefinancingReportItem[];
}

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const columns: ColumnsType<RefinancingReportItem> = [
  {
    title: 'Loan ID',
    dataIndex: 'loanId',
    key: 'loanId',
  },
  {
    title: 'Branch',
    dataIndex: 'branch',
    key: 'branch',
  },
  {
    title: 'Borrower Name',
    dataIndex: 'borrowerName',
    key: 'borrowerName',
  },
  {
    title: 'Date of Original Loan',
    dataIndex: 'dateOfOriginalLoan',
    key: 'dateOfOriginalLoan',
    render: (date: string) => formatDate(date),
  },
  {
    title: 'Amount of Original Loan',
    dataIndex: 'amountOfOriginalLoan',
    key: 'amountOfOriginalLoan',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Refinanced Amount',
    dataIndex: 'refinancedAmount',
    key: 'refinancedAmount',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Pledge Number',
    dataIndex: 'pledgeNumber',
    key: 'pledgeNumber',
  },
  {
    title: 'Refinanced Date',
    dataIndex: 'refinancedDate',
    key: 'refinancedDate',
    render: (date: string | null) => formatDate(date) || '-',
  },
  {
    title: 'Current Principal Outstanding',
    dataIndex: 'currentPrincipalOutstanding',
    key: 'currentPrincipalOutstanding',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Current Interest Outstanding',
    dataIndex: 'currentInterestOutstanding',
    key: 'currentInterestOutstanding',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Total Outstanding',
    dataIndex: 'totalOutstanding',
    key: 'totalOutstanding',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Next Due Date',
    dataIndex: 'nextDueDate',
    key: 'nextDueDate',
    render: (date: string | null) => formatDate(date) || '-',
  },
  {
    title: 'Last Paid Date',
    dataIndex: 'lastPaidDate',
    key: 'lastPaidDate',
    render: (date: string | null) => formatDate(date) || '-',
  },
  {
    title: 'Last Paid Amount',
    dataIndex: 'lastPaidAmount',
    key: 'lastPaidAmount',
    render: (amt: number) => fmt.format(amt),
  },
  {
    title: 'Default Days',
    dataIndex: 'defaultDays',
    key: 'defaultDays',
    render: (days: number | null) => (days !== null && days !== undefined ? days : '-'),
  },
];

const RefinancingReportDisplay: React.FC<RefinancingReportProps> = ({
  data,
}) => {
  return (
    <div className="loan-data-container">
      <h2>Refinancing Report</h2>
      <Table
        columns={columns}
        dataSource={data}
        rowKey={(record) => `${record.loanId}_${record.pledgeNumber}`}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default RefinancingReportDisplay;
